import axios from 'axios';

import { notifySessionExpired } from './sessionExpiry';
import { i18n } from '../i18n';

export const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

/*
 * The URL of an endpoint that belongs to the server as a whole rather
 * than to the office module the base URL points at (`…/api/hs`): the
 * version (`…/api/version`) and the rbac context (`…/api/rbac/…`) sit
 * one level above it. Derived from the configured base by dropping its
 * last segment rather than hardcoded — the host differs per
 * environment (see `.env*`), only the shape is fixed.
 *
 * The result is absolute on purpose: axios then leaves the base URL
 * out of it, and the assumed-roles interceptor, whose rules all match
 * paths below the base, cannot attach a role to such a request.
 */
export const apiSiblingUrl = (
  path,
  base = process.env.REACT_APP_API_BASE_URL
) => {
  const trimmed = String(base ?? '').replace(/\/+$/, '');
  return `${trimmed.replace(/\/[^/]*$/, '')}${path}`;
};

apiClient.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('jwt_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  /* tell the backend which language to localize its responses in (e.g.
     error messages): the language chosen in the app, not just whatever
     Accept-Language the browser would send on its own */
  const language = i18n.resolvedLanguage || i18n.language;
  if (language) {
    config.headers['Accept-Language'] = language;
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    /* an expired/invalid JWT — let the auth provider renew or re-login */
    if (error.response?.status === 401) {
      notifySessionExpired();
    }
    return Promise.reject(error);
  }
);
